import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
   Box,
   Button,
   Typography,
   AppBar,
   Toolbar,
   TextField,
   Link,
   Checkbox,
   FormControlLabel,
   Alert,
   CircularProgress,
   Paper,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import FirstAlbumAlbumStep from '../components/albums/FirstAlbumAlbumStep';
import {
   payloadToSerializedWizard,
   type CreateAlbumFormPayload,
   type SerializedCreateAlbumWizardState,
} from '../components/albums/createAlbumWizardTypes';
import { useAuth } from '../contexts/AuthContext';
import { getAuthHeaders } from '../utils/auth';
import { getAuth } from 'firebase/auth';

const BG = '#080A0F';
const TEAL = '#00C4A7';
const MUTED = 'rgba(255,255,255,0.45)';
const FONT_SERIF = '"Cormorant Garamond", serif';
const FONT_SANS = '"DM Sans", sans-serif';
const FONT_MONO = '"DM Mono", monospace';

const STORAGE_KEY = 'firstAlbumOnboardingDraft';
/** Set after a successful POST /api/albums/create for this onboarding fingerprint (session-scoped). */
const CREATED_FP_KEY = 'firstAlbumOnboardingCreatedFingerprint';

type MainStep = 0 | 1 | 2;

interface PersistedDraft {
   mainStep: MainStep;
   album?: SerializedCreateAlbumWizardState;
   albumPayload?: CreateAlbumFormPayload;
}

function loadDraft(): PersistedDraft | null {
   try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as PersistedDraft;
   } catch {
      return null;
   }
}

function saveDraft(draft: PersistedDraft) {
   try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
   } catch {
      /* ignore */
   }
}

function clearDraft() {
   try {
      sessionStorage.removeItem(STORAGE_KEY);
   } catch {
      /* ignore */
   }
}

function fingerprintOnboardingPayload(
   p: CreateAlbumFormPayload,
   createdBy: string,
): string {
   return [
      createdBy,
      p.title.trim().toLowerCase(),
      p.type,
      p.albumDate || '',
      (p.description || '').trim(),
      p.type === 'paid' ? String(p.pricePerPhoto ?? '') : '',
   ].join('\x1e');
}

function getStoredCreatedFingerprint(): string | null {
   try {
      return sessionStorage.getItem(CREATED_FP_KEY);
   } catch {
      return null;
   }
}

function setStoredCreatedFingerprint(fp: string) {
   try {
      sessionStorage.setItem(CREATED_FP_KEY, fp);
   } catch {
      /* ignore */
   }
}

/** Stable header value for server idempotent replay (length-safe). */
function onboardingIdempotencyKey(p: CreateAlbumFormPayload, createdBy: string): string {
   const fp = fingerprintOnboardingPayload(p, createdBy);
   if (fp.length <= 220) return `firstAlbum|${fp}`;
   return `firstAlbum|${createdBy}|${p.title.trim().slice(0, 64)}|${p.type}|${p.albumDate || ''}`;
}

const GetStartedFirstAlbumPage: React.FC = () => {
   const navigate = useNavigate();
   const { signUp, currentUser } = useAuth();

   const initialDraft = useMemo(() => loadDraft(), []);
   const [mainStep, setMainStep] = useState<MainStep>(initialDraft?.mainStep ?? 0);
   const [albumPayload, setAlbumPayload] = useState<CreateAlbumFormPayload | null>(
      initialDraft?.albumPayload ?? null
   );
   const [albumWizardSeed, setAlbumWizardSeed] = useState<
      Partial<SerializedCreateAlbumWizardState> | undefined
   >(initialDraft?.album);
   const [albumWizardMountId, setAlbumWizardMountId] = useState(0);

   const [fullName, setFullName] = useState('');
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [agreeToTerms, setAgreeToTerms] = useState(false);
   const [submitError, setSubmitError] = useState<string | null>(null);
   const [submitting, setSubmitting] = useState(false);
   /** Blocks double-submit before React re-renders (setSubmitting is async). */
   const submitInFlightRef = useRef(false);

   const persistAlbumWizard = useCallback((album: SerializedCreateAlbumWizardState) => {
      saveDraft({
         mainStep: 1,
         album,
      });
   }, []);

   const handleAlbumComplete = (payload: CreateAlbumFormPayload) => {
      setAlbumPayload(payload);
      const albumSerialized = payloadToSerializedWizard(payload);
      saveDraft({
         mainStep: 2,
         album: albumSerialized,
         albumPayload: payload,
      });
      setMainStep(2);
   };

   const handleBackFromAlbum = () => {
      saveDraft({ mainStep: 0 });
      setMainStep(0);
   };

   const createAlbumRequest = async (payload: CreateAlbumFormPayload, createdBy: string) => {
      const headers = await getAuthHeaders();
      const idemKey = onboardingIdempotencyKey(payload, createdBy);
      const body: Record<string, unknown> = {
         title: payload.title,
         description: payload.description || '',
         type: payload.type,
         enableParticipantParticipation: payload.enableParticipantParticipation,
         createdBy,
         albumDate: payload.albumDate,
         idempotencyKey: idemKey,
      };
      if (payload.type === 'paid') {
         body.pricePerPhoto = payload.pricePerPhoto ?? 5.0;
         body.pricingModel = payload.pricingModel || 'per_photo';
         body.paidAlbumAccessType = payload.paidAlbumAccessType || 'public';
      }

      const apiBase = process.env.REACT_APP_API_URL || '';
      let res: Response;
      try {
         res = await fetch(`${apiBase}/api/albums/create`, {
            method: 'POST',
            headers: {
               ...headers,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
         });
      } catch (e) {
         if (e instanceof TypeError) {
            throw new Error(
               apiBase
                  ? `Cannot reach the API at ${apiBase}. Start the backend and confirm the port matches REACT_APP_API_URL.`
                  : 'REACT_APP_API_URL is not set. Add it to .env (e.g. http://localhost:4001) and restart the dev server.',
            );
         }
         throw e;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
         throw new Error(data.message || data.error || 'Failed to create album');
      }
   };

   /** If this onboarding payload was already created this session, go to feed without POST. */
   const finishOnboardingIfAlreadyCreated = useCallback(
      (payload: CreateAlbumFormPayload, uid: string) => {
         const fp = fingerprintOnboardingPayload(payload, uid);
         if (getStoredCreatedFingerprint() !== fp) return false;
         clearDraft();
         navigate('/feed', { replace: true, state: { onboardingFirstAlbum: true } });
         return true;
      },
      [navigate],
   );

   useEffect(() => {
      if (mainStep !== 2 || !albumPayload || !currentUser?.uid) return;
      finishOnboardingIfAlreadyCreated(albumPayload, currentUser.uid);
   }, [mainStep, albumPayload, currentUser?.uid, finishOnboardingIfAlreadyCreated]);

   const handleSubmitAccount = async () => {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setSubmitError(null);
      try {
         if (!albumPayload) {
            setSubmitError('Album data missing. Go back and complete album setup.');
            return;
         }
         if (!fullName.trim() || !email.trim() || !password.trim()) {
            setSubmitError('Please fill in all fields.');
            return;
         }
         if (password.length < 6) {
            setSubmitError('Password must be at least 6 characters.');
            return;
         }
         if (!agreeToTerms) {
            setSubmitError('Please agree to the Terms of Service.');
            return;
         }

         setSubmitting(true);

         if (currentUser?.uid) {
            if (finishOnboardingIfAlreadyCreated(albumPayload, currentUser.uid)) {
               return;
            }
            await createAlbumRequest(albumPayload, currentUser.uid);
            setStoredCreatedFingerprint(
               fingerprintOnboardingPayload(albumPayload, currentUser.uid),
            );
         } else {
            await signUp(email.trim(), password, fullName.trim());
            const uid = getAuth().currentUser?.uid;
            if (!uid) {
               throw new Error('Account created but session not ready. Please try logging in.');
            }
            if (finishOnboardingIfAlreadyCreated(albumPayload, uid)) {
               return;
            }
            await createAlbumRequest(albumPayload, uid);
            setStoredCreatedFingerprint(fingerprintOnboardingPayload(albumPayload, uid));
         }

         clearDraft();
         navigate('/feed', { replace: true, state: { onboardingFirstAlbum: true } });
      } catch (e) {
         const msg = e instanceof Error ? e.message : 'Something went wrong.';
         setSubmitError(msg);
      } finally {
         submitInFlightRef.current = false;
         setSubmitting(false);
      }
   };

   const handleLoggedInCreateOnly = async () => {
      if (submitInFlightRef.current) return;
      if (!albumPayload || !currentUser?.uid) return;
      submitInFlightRef.current = true;
      setSubmitError(null);
      try {
         if (finishOnboardingIfAlreadyCreated(albumPayload, currentUser.uid)) {
            return;
         }
         setSubmitting(true);
         await createAlbumRequest(albumPayload, currentUser.uid);
         setStoredCreatedFingerprint(
            fingerprintOnboardingPayload(albumPayload, currentUser.uid),
         );
         clearDraft();
         navigate('/feed', { replace: true, state: { onboardingFirstAlbum: true } });
      } catch (e) {
         setSubmitError(e instanceof Error ? e.message : 'Failed to create album');
      } finally {
         submitInFlightRef.current = false;
         setSubmitting(false);
      }
   };

   return (
      <Box sx={{ bgcolor: BG, color: 'white', minHeight: '100vh', overflowX: 'hidden' }}>
         <AppBar
            position='fixed'
            elevation={0}
            sx={{
               bgcolor: 'rgba(8,10,15,0.85)',
               backdropFilter: 'blur(20px)',
               borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
         >
            <Toolbar sx={{ height: 64, px: { xs: 2, md: 4 }, justifyContent: 'space-between' }}>
               <Box
                  component={RouterLink}
                  to='/'
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none' }}
               >
                  <Box
                     sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        flexShrink: 0,
                     }}
                  >
                     <Box
                        component='img'
                        src={logo}
                        alt='WeTrippen'
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                     />
                  </Box>
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: 22,
                        fontWeight: 600,
                        color: TEAL,
                        letterSpacing: -0.3,
                     }}
                  >
                     WeTrippen
                  </Typography>
               </Box>
               <Button
                  component={RouterLink}
                  to={`/login?returnUrl=${encodeURIComponent('/get-started')}`}
                  sx={{
                     fontFamily: FONT_SANS,
                     fontSize: 13,
                     fontWeight: 500,
                     color: 'rgba(255,255,255,0.5)',
                     border: '1px solid rgba(255,255,255,0.12)',
                     px: 2.5,
                     py: 1,
                     '&:hover': { color: 'white', borderColor: 'rgba(255,255,255,0.3)' },
                  }}
               >
                  Sign In
               </Button>
            </Toolbar>
         </AppBar>

         <Box sx={{ pt: 12, pb: 6, px: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
            {mainStep === 0 && (
               <Box sx={{ textAlign: { xs: 'left', md: 'center' }, maxWidth: 640, mx: 'auto' }}>
                  <Typography
                     sx={{
                        fontFamily: FONT_MONO,
                        fontSize: 10,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: TEAL,
                        mb: 2,
                     }}
                  >
                     Your first album
                  </Typography>
                  <Typography
                     component='h1'
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: { xs: 36, sm: 48 },
                        fontWeight: 600,
                        lineHeight: 1.15,
                        mb: 2,
                     }}
                  >
                     Create a home for every moment
                  </Typography>
                  <Typography
                     sx={{
                        fontFamily: FONT_SANS,
                        fontSize: 17,
                        fontWeight: 400,
                        color: MUTED,
                        lineHeight: 1.7,
                        mb: 4,
                     }}
                  >
                     Albums keep guests, contributors, and memories in one place—QR access, optional
                     sales, and a feed that updates as photos arrive. You&apos;ll name your album,
                     choose how it works, then create a free account. Your album appears in your feed
                     right away.
                  </Typography>
                  <Button
                     variant='contained'
                     onClick={() => {
                        const prev = loadDraft();
                        setAlbumWizardSeed(prev?.album);
                        setAlbumWizardMountId((x) => x + 1);
                        setMainStep(1);
                        saveDraft({ ...prev, mainStep: 1, album: prev?.album });
                     }}
                     sx={{
                        fontFamily: FONT_SANS,
                        fontWeight: 700,
                        bgcolor: TEAL,
                        color: '#000',
                        px: 4,
                        py: 1.5,
                        '&:hover': { bgcolor: '#00dfc0' },
                     }}
                  >
                     Start — design my album
                  </Button>
               </Box>
            )}

            {mainStep === 1 && (
               <Paper
                  elevation={0}
                  sx={{
                     bgcolor: 'rgba(20,24,32,0.95)',
                     border: '1px solid rgba(255,255,255,0.08)',
                     borderRadius: 2,
                     p: { xs: 2, sm: 3 },
                  }}
               >
                  <FirstAlbumAlbumStep
                     key={albumWizardMountId}
                     initial={albumWizardSeed}
                     onComplete={handleAlbumComplete}
                     onBackToMarketing={handleBackFromAlbum}
                     onStateChange={persistAlbumWizard}
                  />
               </Paper>
            )}

            {mainStep === 2 && albumPayload && (
               <Paper
                  elevation={0}
                  sx={{
                     bgcolor: 'rgba(20,24,32,0.95)',
                     border: '1px solid rgba(255,255,255,0.08)',
                     borderRadius: 2,
                     p: { xs: 2, sm: 4 },
                     maxWidth: 440,
                     mx: 'auto',
                  }}
               >
                  <Typography
                     sx={{
                        fontFamily: FONT_SERIF,
                        fontSize: 28,
                        fontWeight: 600,
                        mb: 1,
                     }}
                  >
                     {currentUser ? 'Create your album' : 'Create your account'}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_SANS, color: MUTED, mb: 3, fontSize: 15 }}>
                     {currentUser
                        ? 'You’re signed in. We’ll create your album and open your feed.'
                        : 'Last step—your album is ready to be created under your new account.'}
                  </Typography>

                  {submitError && (
                     <Alert severity='error' sx={{ mb: 2 }}>
                        {submitError}
                     </Alert>
                  )}

                  {!currentUser && (
                     <>
                        <TextField
                           label='Full name'
                           value={fullName}
                           onChange={(e) => setFullName(e.target.value)}
                           fullWidth
                           sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)' } }}
                           InputLabelProps={{ sx: { color: MUTED } }}
                           inputProps={{ sx: { color: 'white' } }}
                        />
                        <TextField
                           label='Email'
                           type='email'
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           fullWidth
                           sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)' } }}
                           InputLabelProps={{ sx: { color: MUTED } }}
                           inputProps={{ sx: { color: 'white' } }}
                        />
                        <TextField
                           label='Password'
                           type='password'
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           fullWidth
                           sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)' } }}
                           InputLabelProps={{ sx: { color: MUTED } }}
                           inputProps={{ sx: { color: 'white' } }}
                        />
                        <FormControlLabel
                           control={
                              <Checkbox
                                 checked={agreeToTerms}
                                 onChange={(e) => setAgreeToTerms(e.target.checked)}
                                 sx={{ color: TEAL, '&.Mui-checked': { color: TEAL } }}
                              />
                           }
                           label={
                              <Typography sx={{ fontFamily: FONT_SANS, fontSize: 14, color: MUTED }}>
                                 I agree to the{' '}
                                 <Link href='/terms' target='_blank' sx={{ color: TEAL }}>
                                    Terms
                                 </Link>
                              </Typography>
                           }
                        />
                     </>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                     <Button
                        onClick={() => {
                           if (albumPayload) {
                              const album = payloadToSerializedWizard(albumPayload);
                              setAlbumWizardSeed(album);
                              setAlbumWizardMountId((x) => x + 1);
                              saveDraft({
                                 mainStep: 1,
                                 album,
                                 albumPayload: undefined,
                              });
                           }
                           setMainStep(1);
                        }}
                        sx={{ color: MUTED, textTransform: 'none' }}
                     >
                        Back
                     </Button>
                     <Button
                        variant='contained'
                        onClick={currentUser ? handleLoggedInCreateOnly : handleSubmitAccount}
                        disabled={submitting || (!currentUser && (!agreeToTerms || !fullName.trim()))}
                        sx={{
                           bgcolor: TEAL,
                           color: '#000',
                           fontWeight: 700,
                           px: 3,
                           '&:hover': { bgcolor: '#00dfc0' },
                           '&:disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: MUTED },
                        }}
                     >
                        {submitting ? (
                           <CircularProgress size={22} sx={{ color: '#000' }} />
                        ) : currentUser ? (
                           'Create album & go to feed'
                        ) : (
                           'Create account & album'
                        )}
                     </Button>
                  </Box>

                  {!currentUser && (
                     <Typography sx={{ mt: 2, fontFamily: FONT_SANS, fontSize: 14, color: MUTED }}>
                        Already have an account?{' '}
                        <Link
                           component={RouterLink}
                           to={`/login?returnUrl=${encodeURIComponent('/get-started')}`}
                           sx={{ color: TEAL }}
                        >
                           Log in
                        </Link>
                     </Typography>
                  )}
               </Paper>
            )}
         </Box>
      </Box>
   );
};

export default GetStartedFirstAlbumPage;
